import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

export type DateRangeKind = '24h' | '7d' | '14d' | '1m' | '1y' | 'zero' | 'custom';
export type SmoothingValue = '0' | '12h' | '1d' | '7d' | '14d' | '30d';

export interface GlobalDateRange {
  kind: DateRangeKind;
  customStart?: string;
  customEnd?: string;
}

export interface GlobalTimeSettings {
  dateRange: GlobalDateRange;
  smoothing: SmoothingValue;
}

const DEFAULT_SETTINGS: GlobalTimeSettings = {
  dateRange: { kind: 'zero' },
  smoothing: '0',
};

@Injectable({
  providedIn: 'root',
})
export class GlobalTimeSettingsService {
  private readonly STORAGE_KEY = 'global-time-settings';

  private dateRangeSubject = new BehaviorSubject<GlobalDateRange>(
    DEFAULT_SETTINGS.dateRange
  );
  private smoothingSubject = new BehaviorSubject<SmoothingValue>(
    DEFAULT_SETTINGS.smoothing
  );

  public dateRange$: Observable<GlobalDateRange> =
    this.dateRangeSubject.asObservable().pipe(
      distinctUntilChanged(
        (a, b) =>
          a.kind === b.kind &&
          a.customStart === b.customStart &&
          a.customEnd === b.customEnd
      )
    );
  public smoothing$: Observable<SmoothingValue> =
    this.smoothingSubject.asObservable().pipe(distinctUntilChanged());

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Get current date range value synchronously
   */
  public getCurrentDateRange(): GlobalDateRange {
    return this.dateRangeSubject.getValue();
  }

  /**
   * Get current smoothing value synchronously
   */
  public getCurrentSmoothing(): SmoothingValue {
    return this.smoothingSubject.getValue();
  }

  /**
   * Set the global date range
   */
  public setDateRange(dateRange: GlobalDateRange): void {
    this.dateRangeSubject.next(dateRange);
    this.saveToStorage();
  }

  /**
   * Set the global smoothing value
   */
  public setSmoothing(smoothing: SmoothingValue): void {
    this.smoothingSubject.next(smoothing);
    this.saveToStorage();
  }

  /**
   * Load settings from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const settings: GlobalTimeSettings = JSON.parse(stored);
        if (settings.dateRange) {
          this.dateRangeSubject.next(settings.dateRange);
        }
        if (settings.smoothing) {
          this.smoothingSubject.next(settings.smoothing);
        }
      }
    } catch (error) {
      console.warn('Failed to load global time settings from localStorage:', error);
    }
  }

  /**
   * Save current settings to localStorage
   */
  private saveToStorage(): void {
    try {
      const settings: GlobalTimeSettings = {
        dateRange: this.dateRangeSubject.getValue(),
        smoothing: this.smoothingSubject.getValue(),
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.warn('Failed to save global time settings to localStorage:', error);
    }
  }

  /**
   * Convert DateRangeKind to a Date object for the start date
   */
  public dateRangeKindToStartDate(kind: DateRangeKind): Date {
    const now = new Date();
    const oneDay = 1000 * 60 * 60 * 24;

    switch (kind) {
      case '24h':
        return new Date(Date.now() - oneDay);
      case '7d':
        return new Date(Date.now() - oneDay * 7);
      case '14d':
        return new Date(Date.now() - oneDay * 14);
      case '1m':
        return new Date(new Date().setMonth(now.getMonth() - 1));
      case '1y':
        return new Date(new Date().setFullYear(now.getFullYear() - 1));
      case 'zero':
      default:
        return new Date(0);
    }
  }
}
